import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { ArrowLeft, MapPin, Package, Filter } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { ProductModal } from "@/components/ProductModal";
import { t } from "@/i18n";

export const LocationProductsPage = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const locRes = await axios.get(`${API}/locations`);
        const loc = locRes.data.find((l) => l.id === locationId);
        setLocation(loc);

        const prodRes = await axios.get(`${API}/products?location_id=${locationId}`);
        setProducts(prodRes.data);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [locationId]);

  const categories = useMemo(() => {
    const cats = new Set();
    products.forEach((p) => {
      if (p.category) cats.add(p.category);
    });
    return Array.from(cats).sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    if (selectedCategory === "all") return products;
    return products.filter((p) => p.category === selectedCategory);
  }, [products, selectedCategory]);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen px-6 md:px-12 lg:px-24 py-6">
        <div className="animate-pulse">
          <div className="bg-gray-200 h-8 w-32 rounded mb-4" />
          <div className="bg-gray-200 h-40 rounded-xl mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i}>
                <div className="bg-gray-200 aspect-square rounded-xl mb-3" />
                <div className="bg-gray-200 h-4 rounded w-3/4 mb-2" />
                <div className="bg-gray-200 h-3 rounded w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="px-6 md:px-12 lg:px-24 py-6 border-b border-gray-100">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/locations")}
          className="flex items-center gap-2 text-[#6b7280] hover:text-[#111827] transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">{t('products.backToLocations')}</span>
        </button>

        {location && (
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#1a4d2e] flex items-center justify-center flex-shrink-0">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#111827]">{location.name}</h1>
              <p className="text-[#6b7280]">{location.address}</p>
            </div>
          </div>
        )}
      </div>

      {/* Category Filter */}
      {categories.length > 0 && (
        <div className="px-6 md:px-12 lg:px-24 py-4 border-b border-gray-100 bg-[#f9fafb]">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex items-center gap-2 text-sm text-[#6b7280] flex-shrink-0">
              <Filter className="w-4 h-4" />
              <span>{t('products.filter')}</span>
            </div>
            <button
              data-testid="category-all"
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === "all"
                  ? "bg-[#1a4d2e] text-white"
                  : "bg-white text-[#111827] border border-gray-200 hover:border-[#1a4d2e]"
              }`}
            >
              {t('products.all')} ({products.length})
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`category-${cat}`}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat
                    ? "bg-[#1a4d2e] text-white"
                    : "bg-white text-[#111827] border border-gray-200 hover:border-[#1a4d2e]"
                }`}
              >
                {cat} ({products.filter((p) => p.category === cat).length})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="px-6 md:px-12 lg:px-24 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-[#111827]">
            {selectedCategory === "all" ? t('products.allProducts') : selectedCategory}
          </h2>
          <span className="text-sm text-[#6b7280]">
            {filteredProducts.length} {filteredProducts.length === 1 ? t('products.item') : t('products.items')}
          </span>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-[#f2f4f3] flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-[#9ca3af]" />
            </div>
            <h3 className="font-semibold text-[#111827] mb-1">{t('products.noProducts')}</h3>
            <p className="text-sm text-[#6b7280]">
              {selectedCategory !== "all" ? t('products.noProductsHint') : t('products.checkBackLater')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredProducts.map((product, idx) => (
              <div
                key={idx}
                data-testid={`product-card-${idx}`}
                className="product-card bg-white rounded-xl overflow-hidden cursor-pointer"
                onClick={() => handleProductClick(product)}
              >
                <div className="relative aspect-square bg-[#f2f4f3]">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23f2f4f3' width='200' height='200'/%3E%3Ctext fill='%239ca3af' font-family='sans-serif' font-size='14' x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle'%3EFără Imagine%3C/text%3E%3C/svg%3E";
                    }}
                  />
                  {product.stock === 0 && (
                    <span className="absolute top-3 right-3 bg-gray-800 text-white px-2 py-1 rounded-full text-xs font-medium">
                      {t('products.outOfStock')}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-[#111827] mb-1 line-clamp-2 text-sm">
                    {product.name}
                  </h3>
                  {product.category && (
                    <p className="text-xs text-[#9ca3af] mb-2">
                      {product.category}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="mono font-semibold text-[#1a4d2e]">
                        {product.price.toFixed(2)} {t('products.currency')}
                      </span>
                      {product.sgr > 0 && (
                        <span className="block text-xs text-[#ff4d00] font-medium">
                          +{(product.sgr * 100).toFixed(0)} {t('products.sgrDeposit')}
                        </span>
                      )}
                    </div>
                    {product.stock > 0 && (
                      <span className="text-xs text-[#6b7280]">
                        {product.stock} {t('products.inStock')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Product Modal */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={setIsModalOpen}
        locationName={location?.name}
      />
    </div>
  );
};
